import type { TvChannel } from '@setplex/tria-api'
import type { Store } from 'effector'

import { combine, createEvent, createStore, restore, sample } from 'effector'
import { model as router } from '~/core/router'
import { epgChannelsModel } from '~/entities/epg/channels'
import * as pinLockFeature from '~/features/pin-lock'
import { lookbehind } from '~/shared/effector'
import { QUERY_PARAM } from './index.h'

export const autoSetActiveId = createEvent<{
  channels: TvChannel[]
  query: URLSearchParams
}>()
export const setActiveId = createEvent<number | null>()
export const $activeId = createStore<number | null>(null)
  .on(setActiveId, (_, id) => id)
  .on(autoSetActiveId, (current, { query }) => {
    const id = query.get(QUERY_PARAM)
    if (id == null) return current // do not change current active id
    const idn = Number(id)
    if (isNaN(idn)) return current // do not change current active id
    return idn
  })

//
// persist active channel id in query string
//
export const pushActiveIdToQuery = createEvent<number | null>()

lookbehind({
  source: $activeId,
  clear: autoSetActiveId,
  within: 0,
  target: pushActiveIdToQuery,
})

sample({
  clock: pushActiveIdToQuery,
  fn: (id) => ({ [QUERY_PARAM]: id == null ? null : String(id) }),
  target: router.navigatePushQuery,
})

// currently active channel
export const $active: Store<TvChannel | null> = combine(
  $activeId,
  epgChannelsModel.$all,
  (id, [channels]) => (id == null ? null : (channels.get(id) ?? null))
)

export const $isPinLocked = combine(
  pinLockFeature.$isLockedFn,
  $active,
  (isLockedFn, channel) => isLockedFn(channel)
)

// select first channel from active list, if there is no channel id in url
sample({
  clock: autoSetActiveId,
  source: {
    id: $activeId,
    active: $active,
  },
  filter: ({ id, active }, { channels }) =>
    id == null && active == null && channels.length > 0,
  fn: (_, { channels }) => channels[0].id,
  target: setActiveId,
})

// load channel, if there is no active channel in loaded, but there is active channel id
sample({
  clock: autoSetActiveId,
  source: {
    id: $activeId,
    active: $active,
  },
  filter: ({ id, active }) => id != null && active == null,

  fn: ({ id }) => id!,
  target: epgChannelsModel.loadOneFx,
})

// if there is still no active channel - reset channel id or select first channel from list
sample({
  clock: epgChannelsModel.loadOneFx.finally,
  source: {
    active: $active,
    autoset: restore(autoSetActiveId, null),
  },
  filter: ({ active }) => active == null,
  fn: ({ autoset }) =>
    autoset != null && autoset.channels.length > 0
      ? autoset.channels[0].id
      : null,
  target: setActiveId,
})
